// Libs
import when from 'when'
import Emitter from 'event-emitter'
import { vec2, vec3 } from 'gl-matrix'

// Strcutral
import SceneAbs from '../abstract/scene'
import Camera from '../utils/camera'
import TextureLoader from '../utils/textureLoaderObject'
import Manifest from '../manifest'
import Node from '../abstract/node'


// Entities
import Plane from '../entities/plane'
import Mesh from '../entities/mesh'
import Leaf from '../entities/leafMesh'
import Water from '../entities/water'
import SkinedMesh from '../entities/skining/skinedMesh'
import HandAnimator from '../entities/handAnimator'
import Particles from '../entities/particles'
import ShootingStar from '../entities/shootingStar'

// Post
import DummyPost from '../postProcess/dummyPost'
import Halo from '../postProcess/haloPostProcess'
import PostProcessRT from '../postProcess/postProcessRT'

// Utils
import RAF from '@/js/utils/raf'
import DebugController from '@/glxp/debug/debugController'
import Mouse from '@/js/utils/mouse'
import OrbitController from '@/glxp/camera/orbit'

// Managers
import LoadingManager from './mainSceneLoadingManager'
import AnimationManager from './mainSceneAnimationManager'
import WebsocketManager from '@/js/websocketManager.js'
import TensorManager from "@/tensor/tensorManager"

import { TimelineMax, TweenMax, Power2, Power3, Power4 } from "gsap"


const _VEC2 = vec2.create()

class Scene extends SceneAbs {
  constructor (container, manager = null) {
    super(container, manager)

    this.manager                      = manager

    this.cameraPositionTarget         = vec3.fromValues(0, 0, 0)
    this.cameraPosition               = vec3.fromValues(0, 0, 0)
    this.cameraDirection              = vec3.create()

    this.camera                       = new Camera(this, 30)
    this.root.addChildNode(this.camera.node)

    this.orbit                        = new OrbitController(this, this.camera)
    
    this.clearColor                   = [0, 0, 0, 0]
    this.loaded                       = false
    this.preloaded                    = false
    this.timeScale                    = 0
    this.cameraFreedom                = 0
    
    this.halo                         = new Halo(this)
    this.handsReflectRT               = new PostProcessRT(this, 0.5)
    this.textureLoader                = new TextureLoader(this)
    
    this.root.scale = vec3.fromValues(0.05, 0.05, 0.05)
    
    this.animationManager = new AnimationManager(this)

    this.shake = false
    this.nodes = []
    this.meshes = []
    this.splines = []
    this.nenuphars = []
    this.cameraTargets = [[], [], [], [], []]
    this.cameraTargetsLookat = [[], [], [], [], []]
    this.activeScenes = ["global", "Scene_1"]

    this.loadingManager = new LoadingManager(this)
    this.initDebug()

  }

  initDebug(){

    window.addEventListener("resize", this.onResizeCustom.bind(this))

    // debug dev
    if (DebugController.active) { 
      document.getElementById('app').classList.add('debug')
      document.querySelector('.gui-custom').style.display = 'none'
      document.addEventListener('keydown', (evt) => {
        if (this.animationManager.plateformVisible == false) {
          this.animationManager.showPlateform()
        }
        if (evt.key == "0") {
          this.animationManager.goToStage(0)
        }
        if (evt.key == "1") {
          this.animationManager.goToStage(1)
        }
        if (evt.key == "2") {
          this.animationManager.goToStage(2)
        }
        if (evt.key == "3") {
          this.animationManager.goToStage(3)
        }
        if (evt.key == "q") {
          this.animationManager.showHandRig(0)
          this.handsRigs[1].mesh.setColor("Hand_Green")
          setTimeout(() => {
            this.animationManager.showHandRig(1)
            this.animationManager.moveHandToFightPosition(0)
            this.animationManager.moveHandToFightPosition(1)
          }, 300)
        
        }
        if (evt.key == "e") {
          this.animationManager.shakeCam()
        }
        if (evt.key == "r") {
          this.animationManager.blinkHand(0)
        }
        if (evt.key == "t") {
          this.animationManager.rippleWater()
        }
        if (evt.key == "w") {
          this.handsRigs[0].animator.playShifumiAnimation()
        }
      })
    }

    // Debug
    this.debugCam = false
    this.config = {
      grain: { value: 0.8, range: [0, 3] },
      grainScale: { value: 0.081, range: [0, 0.5000] },
      grainScaleMesh: { value: 3.1, range: [0, 10] },
      grainScaleWater: { value: 4, range: [0, 10] },
      grainHand: { value: 1.2, range: [0, 3] },
      grainScaleHand: { value: 1.3, range: [0, 10] },
    }
    let panel = DebugController.addConfig(this.config, 'Scene')
    let tmp = { changeCamera: () => { this.debugCam = !this.debugCam } }
    panel.add(tmp, 'changeCamera')

    this.plateformConfig = {
      color1_Step1: { value: [255, 156, 85], guiType: "color" },
      color2_Step1: { value: [96, 176, 211], guiType: "color" },
      color1_Step2: { value: [106, 128, 150], guiType: "color" },
      color2_Step2: { value: [10, 23, 35], guiType: "color" },
      grain: { value: 0.7, range: [0, 3] },
      grainScale: { value: 2, range: [0, 10] },
      gradientRotation: { value: 0.01, range: [0, 6.28] },
      opacityTop: { value: .42, range: [0, 1] },
      opacityBottom: { value: .4, range: [0, 1] },
    }
    DebugController.addConfig(this.plateformConfig, 'Plateform')

  }

  onLoaded () {
    this.active = true

    for (let i = 0; i < this.handsRigs.length; i++) {
      this.handsRigs[i].animator.animations = JSON.parse(JSON.stringify(this.handsAnimations))
      this.handsRigs[i].animator.setAnim("idle-01")
      this.handsRigs[i].animator.onLoaded()
    }

    WebsocketManager.on("roomcomplete", (infos) => {
      this.onRoomComplete()
    })

    this.lookatNode = new Node()
    this.lookatNode.position = vec3.clone(this.cameraTargetsLookat[4])

    this._emitter.emit('loaded')
  }

  onPreloadedLoaded(){
    this.particles = new Particles(this)
    this.shootingStar = new ShootingStar(this)
    this.root.addChildNode(this.particles.node)
    this.root.addChildNode(this.shootingStar.node)
    this.preloaded = true

    requestAnimationFrame(()=>{
      this.renderFirstFrame()
    })
    
  }

  setHandTarget(state, id = 0){
    if (state == "idle") {
      this.handsRigs[id].animator.goToState("idle-01")
    } else if (state == "rock") {
      this.handsRigs[id].animator.goToState("rock")
    } else if (state == "paper") {
      this.handsRigs[id].animator.goToState("paper")
    } else if (state == "scissors") {
      this.handsRigs[id].animator.goToState("scisors")
    }
  }

  goToStage(id){
    this.animationManager.goToStage(id)
  }

  start() {
    // TensorManager.desactivate()
    TensorManager.hideFeedBack()
    this.animationManager.start()    
  }

  unStart(){
    this.camera.node.needUpdate = false
    this.camera.node.lookAt(this.getNode("Target-Intro").position)
    this.camera.updateProjection(this.width / this.height)
    this.animationManager.unStart()    
  }

  startIntro(){
    this.animationManager.startIntro()
    this.animationManager.shootStar()

  }

  activateTrackingAnimations(){
    TensorManager.on("signFound", this.listenerSign = (sign) => {
      if (sign == "none") {
        this.setHandTarget("idle")
      } else {
        this.setHandTarget(sign)
      }
    })
  }

  desactivateTrackingAnimations(){
    if (this.listenerSign) { 
      TensorManager.off("signFound", this.listenerSign)
    }
  }

  preload(){
    let defer = when.defer()
    let loadables = this.loadingManager.getIntroLoadables()
    for (const key in Manifest.intro) {
      if (Manifest.intro.hasOwnProperty(key)) {
        const element = Manifest.intro[key].url
        loadables.push(this.textureLoader.load(element, key, Manifest.intro[key].options))
      }
    }

    when.all(loadables).then(() => {
      this.onPreloadedLoaded()
      defer.resolve()
    })

    return defer.promise
  }

  onResizeCustom(){
    if (this.preloaded && !this.loaded) {
      requestAnimationFrame(() => {
        this.renderFirstFrame()
      })
    }
  }

  load () {
    let loadables = this.loadingManager.getLoadables()

    for (const key in Manifest.home) {
      if (Manifest.home.hasOwnProperty(key)) {
        const element = Manifest.home[key].url
        loadables.push(this.textureLoader.load(element, key, Manifest.home[key].options))
      }
    }

    when.all(loadables).then(() => {
      this.onLoaded()
    })

    let percent = 0
    for (let i = 0; i < loadables.length; i++) {
      loadables[i].then(() => {
        percent++
        this._emitter.emit('progress', Math.round((percent / loadables.length) * 100))
      })
    }
  }

  getMesh(name){
    for (let i = 0; i < this.meshes.length; i++) {
      if (this.meshes[i].name == name) {
        return this.meshes[i]
      }
    }
  }

  getSpline(name){
    for (let i = 0; i < this.splines.length; i++) {
      if (this.splines[i].name == name) {
        return this.splines[i]
      }
    }
  }

  getNode(name){
    for (let i = 0; i < this.nodes.length; i++) {
      if (this.nodes[i].name == name) {
        return this.nodes[i]
      }
    }
  }

  applyDefaultState () {
    let gl = this.gl
    gl.enable(gl.BLEND)
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
    // gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.enable(gl.DEPTH_TEST)
    gl.depthMask(true)
    gl.disable(gl.CULL_FACE)
  }

  onRoomComplete(){}

  // Pre-render
  preRender () {

    let ratio = this.width / this.height
    if (ratio > 3.3) {
      this.camera.fov = 30 - ((ratio - 3.3) * 10)
    } else {
      this.camera.fov = 30
    }

    this.camera.updateProjection(ratio)
    this.cameraPositionTarget[0] = - Mouse.cursor[0] * 15 + Math.sin((this.time + Math.PI * 1.293) * 1.05) * 20
    this.cameraPositionTarget[1] =   Mouse.cursor[1] * 10 + Math.cos((this.time + Math.PI * 0.739) * .9) * 10

    let tmp = vec3.create()
    vec3.sub(tmp, this.cameraPositionTarget, this.cameraPosition)
    vec3.scale(tmp, tmp, 0.05)
    vec3.add(this.cameraPosition, this.cameraPosition, tmp)

    this.camera.node.position[0] = (this.cameraPosition[0] * this.cameraFreedom) + this.camera.node.initialPosition[0]
    this.camera.node.position[1] = (this.cameraPosition[1] * this.cameraFreedom) + this.camera.node.initialPosition[1]
    this.camera.node.position[2] = this.camera.node.initialPosition[2]

    if (this.shake == true) {
      this.camera.node.position[0] += (Math.random() -.5) * 8
      this.camera.node.position[1] += (Math.random() -.5) * 8
    }

    this.camera.node.needUpdate = false
    this.camera.node.lookAt(this.lookatNode.position)

    this.animationManager.preRender()

    for (let i = 0; i < this.handsRigs.length; i++) {
      this.handsRigs[i].animator.update()
      this.handsRigs[i].mesh.preRender()
    }

    if (this.debugCam) {
      this.orbit.update()
    }

  }

  renderFirstFrame(){
    
    let gl = this.gl

    gl.viewport(0, 0, this.width, this.height)
    gl.clearColor(this.clearColor[0], this.clearColor[1], this.clearColor[2], this.clearColor[3])
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
    gl.colorMask(true, true, true, true)

    this.camera.getViewMatrix()
    this.camera.getProjectionMatrix()
    this.camera.node.needUpdate = false
    this.camera.node.lookAt(this.getNode("Target-Intro").position)
    this.camera.updateProjection(this.width / this.height)

    this.background.render()
    this.particles.render()
    this.shootingStar.render()
    for (let i = 0; i < this.meshes.length; i++) {
      this.meshes[i].render(0)
    }

  }

  // Render
  render () {

    if (!this.active) { return }

    this.time += (RAF.dt / 1000) * this.timeScale
    this.dt = RAF.dt * this.timeScale

    let gl = this.gl

    gl.clearColor(this.clearColor[0], this.clearColor[1], this.clearColor[2], this.clearColor[3])
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)
    gl.colorMask(true, true, true, true)

    this.preRender()

    // Render Time

    // For reflection
    this.handsReflectRT.preRender()
    for (let i = 0; i < this.handsRigs.length; i++) {
      let reflectionOffset = ((this.handsRigs[i].rootNode.position[1] - this.handsRootNode.position[1]) * this.root.scale[0]) + (this.handsRootNode.position[1] * this.root.scale[1] * 3)
      this.handsRigs[i].mesh.node.getMatrix()
      this.handsRigs[i].mesh.node.matrix[5] *= -1
      this.handsRigs[i].mesh.node.matrix[13] += reflectionOffset
      this.handsRigs[i].mesh.render()
      this.handsRigs[i].mesh.node.matrix[5] *= -1
      this.handsRigs[i].mesh.node.matrix[13] -= reflectionOffset
    }
    this.handsReflectRT.postRender()
    gl.viewport(0, 0, this.width, this.height)

    // For halo
    this.halo.preRender()
    for (let i = 0; i < this.handsRigs.length; i++) {
      if (this.handsRigs[i].mesh.preventPost == false) {
        this.handsRigs[i].mesh.render()
      }
    }
    this.halo.postRender()


    // this.post.preRender()
    this.background.render()
    this.particles.render()
    this.shootingStar.render()
    
    for (let i = 0; i < this.meshes.length; i++) {
      this.meshes[i].render(0)
    }
    for (let i = 0; i < this.meshes.length; i++) {
      this.meshes[i].render(1.5)
    }
    
    for (let i = 0; i < this.handsRigs.length; i++) {
      this.handsRigs[i].mesh.render()
    }
    
    for (let i = 0; i < this.meshes.length; i++) {
      this.meshes[i].render(1)
    }

    for (let i = 0; i < this.meshes.length; i++) {
      this.meshes[i].render(2)
    }
    
    // this.post.postRender()
    // this.post.render()

    this.postRender()

  }

  postRender () {
    this.gl.viewport(0, 0, this.width, this.height)

    this.halo.render()
    // this.grain.render()
  }
}
export default Scene
